import { useState, useEffect } from 'react'
import { db, auth } from '../lib/supabase'
import { useToast } from '../hooks/useToast'
import Modal from '../components/Modal'

// Import panel components
import SpacesSidebar from '../components/SpacesSidebar'
import ChannelsSidebar from '../components/ChannelsSidebar'
import ChatArea from '../components/ChatArea_v2'  // v2 with YouTube detection + translation
import SettingsModal from '../components/SettingsModal'

// Import Glass layout
import FloatingHUDLayout from '../components/FloatingHUDLayout'

// Import v2.5 components
import VoiceSettingsPanel from '../components/VoiceSettingsPanel'
import EnhancedUserProfile from '../components/EnhancedUserProfile'
import VideoCallModal from '../components/VideoCallModal'

// Import notification components
import { useNotifications } from '../hooks/useNotifications'
import NotificationSettings from '../components/NotificationSettings'

// Import media components
import MediaChannel from '../components/MediaChannel'
import YouTubePlayer from '../components/YouTubePlayer'
import { useYouTubeShareContext } from '../contexts/YouTubeShareContext'

// Import keybinds hook
import { useKeybinds } from '../hooks/useKeybinds'

// Import voice channel presence hook
import { useVoiceChannelPresence } from '../hooks/useVoiceChannelPresence'

/**
 * MainApp - The core 3-panel layout
 * Panel 1 (Left): Spaces/DMs navigation
 * Panel 2 (Middle): Channels/Friends list
 * Panel 3 (Right/Main): Chat/Content area
 */
export default function MainApp({ user, profile }) {
  // Navigation state - restore from localStorage if available
  const [selectedSpace, setSelectedSpace] = useState(null)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('gjyl-view-mode') || 'spaces'
    }
    return 'spaces'
  })

  // Track saved IDs for restoring after data loads
  const [savedSpaceId] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('gjyl-selected-space-id')
    }
    return null
  })
  const [savedChannelId] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('gjyl-selected-channel-id')
    }
    return null
  })

  // Data state
  const [spaces, setSpaces] = useState([])
  const [channels, setChannels] = useState([])
  const [friends, setFriends] = useState([])

  // UI state
  const [showSettings, setShowSettings] = useState(false)
  const [showVoiceSettings, setShowVoiceSettings] = useState(false)
  const [showUserProfile, setShowUserProfile] = useState(false)
  const [selectedProfileUserId, setSelectedProfileUserId] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  // UI Style state (standard or glass)
  const [uiStyle, setUiStyle] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('gjyl-ui-style')
      if (saved === 'glass') return 'jarvis' // migrate old value
      if (saved === 'jarvis' || saved === 'standard') return saved
    }
    return 'standard'
  })

  // Call state
  const [activeCall, setActiveCall] = useState(null) // { type: 'voice'|'video', userId, user }
  const [incomingCall, setIncomingCall] = useState(null)
  const [isInVoiceChannel, setIsInVoiceChannel] = useState(false) // Track if user is in a voice channel

  // Notification state
  const [showNotifications, setShowNotifications] = useState(false)
  const [showNotificationSettings, setShowNotificationSettings] = useState(false)

  // Toast notifications
  const toast = useToast()

  // Use notifications hook
  const {
    notifications,
    loading: notificationsLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotifications(user.id)

  // Use keybinds hook for global keyboard shortcuts
  // This registers Ctrl+Shift+M (mute), Ctrl+Shift+D (deafen), Ctrl+, (settings), Ctrl+K (search)
  useKeybinds({
    onOpenSettings: () => setShowSettings(true),
    onSearch: () => {
      // Focus the search input in ChannelsSidebar (trigger search modal)
      // For now, just open settings to keybinds tab as a fallback
      // TODO: Implement global search modal
      console.log('Search shortcut triggered')
    }
  })

  // Use voice channel presence hook to track users in voice/media channels
  const { channelUsers, channelPresence } = useVoiceChannelPresence(selectedSpace?.id, channels)

  // Build channelSessions map for ChannelsSidebar (startTime and sessionId per channel)
  const channelSessions = Object.fromEntries(
    Object.entries(channelPresence).map(([channelId, presence]) => [
      channelId,
      { startTime: presence.sessionStarted, sessionId: presence.sessionId }
    ])
  )

  // Track if current user is in any voice/media channel (for YouTube sharing prompt)
  // This is true when either:
  // 1. User is tracked in channelPresence (via room_members)
  // 2. User is currently viewing a media type channel
  useEffect(() => {
    const isInAnyVoiceChannel = Object.values(channelPresence).some(presence =>
      presence.users?.some(u => u.id === user?.id)
    )
    const isViewingMediaChannel = selectedChannel?.type === 'media' || selectedChannel?.type === 'voice'
    setIsInVoiceChannel(isInAnyVoiceChannel || isViewingMediaChannel)
  }, [channelPresence, user?.id, selectedChannel?.type])

  // YouTube sharing context - for "Play Together" feature
  const youtubeShare = useYouTubeShareContext()

  // Join YouTube sync channel when entering a channel
  useEffect(() => {
    if (youtubeShare?.joinChannel) {
      youtubeShare.joinChannel(selectedChannel?.id || null)
    }
  }, [selectedChannel?.id, youtubeShare])

  // Listen for UI style changes from Settings
  useEffect(() => {
    const handleUiStyleChange = (e) => {
      const newStyle = e.detail === 'glass' ? 'jarvis' : e.detail
      if (newStyle === 'standard' || newStyle === 'jarvis') {
        setUiStyle(newStyle)
      }
    }
    window.addEventListener('ui-style-change', handleUiStyleChange)
    return () => window.removeEventListener('ui-style-change', handleUiStyleChange)
  }, [])

  // Persist navigation state to localStorage
  useEffect(() => {
    if (selectedSpace?.id) {
      localStorage.setItem('gjyl-selected-space-id', selectedSpace.id)
    }
  }, [selectedSpace?.id])

  useEffect(() => {
    if (selectedChannel?.id) {
      localStorage.setItem('gjyl-selected-channel-id', selectedChannel.id)
    } else {
      // Clear saved channel when deselected
      localStorage.removeItem('gjyl-selected-channel-id')
    }
  }, [selectedChannel?.id])

  useEffect(() => {
    localStorage.setItem('gjyl-view-mode', viewMode)
  }, [viewMode])

  // Load user's spaces on mount
  useEffect(() => {
    loadSpaces()
    loadFriends()
  }, [user.id])

  // Load channels when a space is selected
  useEffect(() => {
    if (selectedSpace) {
      // CRITICAL: Reset selectedChannel when switching spaces
      // This prevents media channels from persisting when navigating between spaces
      setSelectedChannel(null)
      loadChannels(selectedSpace.id)
    }
  }, [selectedSpace])

  const loadSpaces = async () => {
    setLoading(true)
    const { data, error } = await db.spaces.list(user.id)

    if (error) {
      console.error('Error loading spaces:', error)
      setSpaces([])
    } else {
      setSpaces(data || [])
      // Restore saved space or auto-select first space
      if (data && data.length > 0 && !selectedSpace) {
        // Try to restore the previously selected space
        if (savedSpaceId) {
          const savedSpace = data.find(s => s.id === savedSpaceId)
          if (savedSpace) {
            setSelectedSpace(savedSpace)
          } else {
            // Saved space no longer exists, select first
            setSelectedSpace(data[0])
          }
        } else {
          setSelectedSpace(data[0])
        }
      }
    }
    setLoading(false)
  }

  const loadChannels = async (spaceId) => {
    const { data, error } = await db.channels.list(spaceId)

    if (error) {
      console.error('Error loading channels:', error)
      setChannels([])
    } else {
      setChannels(data || [])
      // Restore saved channel if it belongs to this space
      if (savedChannelId && data && data.length > 0) {
        const savedChannel = data.find(c => c.id === savedChannelId)
        if (savedChannel) {
          setSelectedChannel(savedChannel)
        }
      }
      // Otherwise don't auto-select - let user see the welcome screen
    }
  }

  const loadFriends = async () => {
    const { data, error } = await db.friendships.list(user.id)

    if (error) {
      console.error('Error loading friends:', error)
      setFriends([])
    } else {
      setFriends(data || [])
    }
  }

  const handleSignOut = async () => {
    await auth.signOut()
    window.location.href = '/'
  }

  const handleCreateSpace = async (spaceName) => {
    try {
      // Use RPC function - handles UUID generation, member creation, default channels
      const { data: spaceId, error } = await db.spaces.create(spaceName)

      if (error) {
        console.error('Error creating space:', error)
        // Check for specific error types and give helpful messages
        if (error.message?.includes('unique') || error.message?.includes('duplicate') || error.message?.includes('idx_spaces_one_per_user')) {
          toast.error('You already own a space. Each user can only create one space.')
        } else if (error.message?.includes('permission') || error.message?.includes('denied')) {
          toast.error('You don\'t have permission to create a space.')
        } else {
          toast.error('Failed to create space. Please try again.')
        }
        return
      }

      // Reload spaces to get the new space with all its data
      await loadSpaces()
      toast.success(`Space "${spaceName}" created!`)
    } catch (error) {
      console.error('Error creating space:', error)
      // Check for specific error types
      if (error.message?.includes('unique') || error.message?.includes('duplicate')) {
        toast.error('You already own a space. Each user can only create one space.')
      } else {
        toast.error('Failed to create space. Please try again.')
      }
    }
  }

  const handleJoinSpace = async (inviteCode) => {
    try {
      // Use RPC function to join by invite code
      const { data: spaceId, error } = await db.spaces.joinByInvite(inviteCode)

      if (error) {
        // Check for specific error messages
        if (error.message?.includes('already a member')) {
          toast.warning('You are already a member of this space!')
        } else if (error.message?.includes('Invalid') || error.message?.includes('not found')) {
          toast.error('Invalid invite code!')
        } else {
          console.error('Error joining space:', error)
          toast.error('Failed to join space. Please try again.')
        }
        return
      }

      // Success - reload spaces from database
      await loadSpaces()
      toast.success('Successfully joined space!')
    } catch (error) {
      console.error('Error joining space:', error)
      toast.error('Failed to join space. Please try again.')
    }
  }

  const handleSwitchUiStyle = (style) => {
    if (style !== 'standard' && style !== 'jarvis') return
    setUiStyle(style)
    try {
      localStorage.setItem('gjyl-ui-style', style)
      window.dispatchEvent(new CustomEvent('ui-style-change', { detail: style }))
    } catch (e) {
      console.error('Failed to persist ui style', e)
    }
  }

  if (uiStyle === 'jarvis') {
    return <FloatingHUDLayout onSwitchStyle={handleSwitchUiStyle} />
  }

  const handleUpdateSpace = (updatedSpace) => {
    // Update the space in the spaces list
    setSpaces(prevSpaces =>
      prevSpaces.map(space =>
        space.id === updatedSpace.id ? updatedSpace : space
      )
    )

    // If the updated space is currently selected, update it too
    if (selectedSpace?.id === updatedSpace.id) {
      setSelectedSpace(updatedSpace)
    }
  }

  const handleDeleteSpace = (spaceId) => {
    // Remove the space from the spaces list
    setSpaces(prevSpaces => prevSpaces.filter(space => space.id !== spaceId))

    // Clear selection if the deleted space was selected
    if (selectedSpace?.id === spaceId) {
      setSelectedSpace(null)
      setSelectedChannel(null)
    }
  }

  const handleOpenProfile = (userId = null) => {
    setSelectedProfileUserId(userId || user.id)
    setShowUserProfile(true)
  }

  const handleUpdateStatus = async (newStatus) => {
    try {
      const { error } = await db.profiles.update(user.id, {
        custom_status: newStatus
      })
      if (error) throw error
      toast.success('Status updated!')
    } catch (err) {
      console.error('Error updating status:', err)
      toast.error('Failed to update status')
    }
  }

  const handleStartDM = (friendId) => {
    // TODO: Implement DM channel creation and navigation
    console.log('Starting DM with:', friendId)
    setViewMode('dms')
    toast.info('DM feature coming soon - will create/navigate to DM channel')
  }

  const handleStartVoiceCall = async (friendId) => {
    // Get friend's profile using db helper
    const { data: friendProfile } = await db.profiles.get(friendId)

    setActiveCall({
      type: 'voice',
      userId: friendId,
      user: friendProfile || { id: friendId, username: 'Friend' }
    })
  }

  const handleStartVideoCall = async (friendId) => {
    // Get friend's profile using db helper
    const { data: friendProfile } = await db.profiles.get(friendId)

    setActiveCall({
      type: 'video',
      userId: friendId,
      user: friendProfile || { id: friendId, username: 'Friend' }
    })
  }

  const handleSearchResultSelect = (type, item) => {
    console.log('Search result selected:', type, item)

    switch (type) {
      case 'user':
        // Open user profile
        handleOpenProfile(item.id)
        break
      case 'space':
        // Select the space
        setSelectedSpace(item)
        setViewMode('spaces')
        break
      case 'channel':
        // Select the channel (would need to load its space first in real implementation)
        setSelectedChannel(item)
        break
      case 'chat':
        // Navigate to DM
        setViewMode('dms')
        // In real implementation, would set selected DM channel
        break
      default:
        console.log('Unknown search result type:', type)
    }
  }

  const handleNotificationClick = (actionUrl) => {
    // Navigate to action URL if provided
    if (actionUrl) {
      console.log('Navigate to:', actionUrl)
      // In a real implementation, would parse the URL and navigate accordingly
      // e.g., /space/123/channel/456 or /dm/user789
    }
    // Close notifications dropdown
    setShowNotifications(false)
  }

  // Glass UI Layout
  if (uiStyle === 'glass') {
    return (
      <FloatingHUDLayout
        spaces={spaces}
        channels={channels}
        members={friends.map(f => ({
          id: f.id,
          username: f.username || f.display_name || 'User',
          avatar: f.avatar_url,
          status: f.status || 'online',
          activity: f.activity
        }))}
        activeSpaceId={selectedSpace?.id}
        activeChannelId={selectedChannel?.id}
        onSpaceSelect={(spaceId) => {
          const space = spaces.find(s => s.id === spaceId)
          if (space) setSelectedSpace(space)
        }}
        onChannelSelect={(channelId) => {
          const channel = channels.find(c => c.id === channelId)
          if (channel) setSelectedChannel(channel)
        }}
        currentUser={profile}
      >
        {/* Main content area */}
        <div className="flex-1 flex flex-col h-full">
          {selectedChannel?.type === 'media' ? (
            <MediaChannel
              channel={selectedChannel}
              space={selectedSpace}
              user={user}
              profile={profile}
            />
          ) : (
            <ChatArea
              selectedChannel={selectedChannel}
              selectedSpace={selectedSpace}
              user={user}
              profile={profile}
              isInVoiceCall={isInVoiceChannel || !!activeCall}
            />
          )}
        </div>

        {/* All modals work in glass mode too */}
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          user={user}
          profile={profile}
          onUpdateProfile={(updatedProfile) => {
            console.log('Profile updated:', updatedProfile)
          }}
          onSignOut={handleSignOut}
        />

        <VoiceSettingsPanel
          userId={user.id}
          isOpen={showVoiceSettings}
          onClose={() => setShowVoiceSettings(false)}
        />

        <EnhancedUserProfile
          userId={user.id}
          profileUserId={selectedProfileUserId}
          currentUserProfile={profile}
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
          onStartDM={handleStartDM}
          onStartVoiceCall={handleStartVoiceCall}
          onStartVideoCall={handleStartVideoCall}
          onUpdateProfile={(updatedProfile) => {
            // Dispatch event to notify parent (AuthGuard) to refresh profile
            window.dispatchEvent(new CustomEvent('profile-updated', { detail: updatedProfile }))
          }}
        />

        {activeCall && (
          <VideoCallModal
            isOpen={true}
            onClose={() => setActiveCall(null)}
            callType={activeCall.type}
            otherUser={activeCall.user}
            currentUser={profile}
          />
        )}
      </FloatingHUDLayout>
    )
  }

  // Standard UI Layout
  return (
    <div className="flex h-screen bg-gjyl-darker overflow-hidden">
      {/* Panel 1: Spaces/DMs Sidebar (Leftmost - w-16 on desktop) */}
      <SpacesSidebar
        spaces={spaces}
        selectedSpace={selectedSpace}
        onSelectSpace={setSelectedSpace}
        viewMode={viewMode}
        onChangeViewMode={setViewMode}
        profile={profile}
        onOpenSettings={() => setShowSettings(true)}
        onOpenProfile={() => handleOpenProfile(user.id)}
        onOpenVoiceSettings={() => setShowVoiceSettings(true)}
        onSignOut={handleSignOut}
        onCreateSpace={handleCreateSpace}
        onJoinSpace={handleJoinSpace}
        onUpdateSpace={handleUpdateSpace}
        onDeleteSpace={handleDeleteSpace}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
        toast={toast}
        // Notification props - now tied to profile dock
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        onUpdateStatus={handleUpdateStatus}
        // Style switching - logo click goes to Jarvis
        onSwitchStyle={handleSwitchUiStyle}
      />

      {/* Panel 2: Channels/Friends Sidebar (Middle - w-60 on desktop) */}
      <ChannelsSidebar
        viewMode={viewMode}
        selectedSpace={selectedSpace}
        channels={channels}
        selectedChannel={selectedChannel}
        onSelectChannel={setSelectedChannel}
        friends={friends}
        profile={profile}
        mobileMenuOpen={mobileMenuOpen}
        userId={user.id}
        onSearchResultSelect={handleSearchResultSelect}
        toast={toast}
        channelUsers={channelUsers}
        channelSessions={channelSessions}
      />

      {/* Panel 3: Main Content Area (Chat or Media) */}
      {selectedChannel?.type === 'media' ? (
        <MediaChannel
          channel={selectedChannel}
          space={selectedSpace}
          user={user}
          profile={profile}
        />
      ) : (
        <ChatArea
          selectedChannel={selectedChannel}
          selectedSpace={selectedSpace}
          user={user}
          profile={profile}
          isInVoiceCall={isInVoiceChannel || !!activeCall}
        />
      )}

      {/* YouTube Player - floating overlay when video is shared */}
      {youtubeShare?.activeVideo && (
        <div className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)]">
          <YouTubePlayer
            videoId={youtubeShare.activeVideo.videoId}
            title={youtubeShare.activeVideo.title}
            sharedBy={youtubeShare.activeVideo.sharedBy}
            onClose={() => youtubeShare.stopVideo()}
            onPlayStateChange={(state, time) => youtubeShare.updatePlayback(state, time)}
            syncedState={youtubeShare.playbackState}
            syncedTime={youtubeShare.currentTime}
            isHost={youtubeShare.isHost}
          />
        </div>
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        user={user}
        profile={profile}
        onUpdateProfile={(updatedProfile) => {
          // Update local profile state
          // In a real app, this would trigger a refetch
          console.log('Profile updated:', updatedProfile)
        }}
        onSignOut={handleSignOut}
      />

      {/* Voice & Video Settings Panel */}
      <VoiceSettingsPanel
        userId={user.id}
        isOpen={showVoiceSettings}
        onClose={() => setShowVoiceSettings(false)}
      />

      {/* Enhanced User Profile Modal */}
      <EnhancedUserProfile
        userId={user.id}
        profileUserId={selectedProfileUserId}
        currentUserProfile={profile}
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        onStartDM={handleStartDM}
        onStartVoiceCall={handleStartVoiceCall}
        onStartVideoCall={handleStartVideoCall}
        onUpdateProfile={(updatedProfile) => {
          // Dispatch event to notify parent (AuthGuard) to refresh profile
          window.dispatchEvent(new CustomEvent('profile-updated', { detail: updatedProfile }))
        }}
      />

      {/* Video/Voice Call Modal */}
      {activeCall && (
        <VideoCallModal
          isOpen={true}
          onClose={() => setActiveCall(null)}
          callType={activeCall.type}
          otherUser={activeCall.user}
          currentUser={profile}
        />
      )}

      {/* Notification Settings Modal - triggered from profile dock bubble */}
      <NotificationSettings
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        userId={user.id}
      />
    </div>
  )
}
